import { Button, Grid, Stack } from '@mui/material'
import difference from 'lodash/difference'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'

import { LoadingButton } from 'components/common/LoadingButton'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import { MonitorDetails } from 'components/status-checks/common/MonitorDetails'
import { MonitorDetailsDatadog } from 'components/status-checks/common/MonitorDetailsDatadog'
import { StatusCheckConnectionForm } from 'components/status-checks/common/StatusCheckConnectionForm'
import { getIsDynatraceIntelligentHealthCheckRequest } from 'components/status-checks/create/create-status-check-utils'
import { useStatusCheckRequestBuilder } from 'components/status-checks/hooks/use-status-check-request-builder'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import toast from 'lib/toast'
import { useCreateDynatraceStatusCheck } from 'queries/status-checks/use-create-dynatrace-status-check'
import { useCreateStatusCheck } from 'queries/status-checks/use-create-status-check'
import { useGetStatusChecks } from 'queries/status-checks/use-get-status-checks'
import { usePutStatusCheck } from 'queries/status-checks/use-put-status-check'
import { useUpdateDynatraceStatusCheck } from 'queries/status-checks/use-update-dynatrace-status-check'
import { type ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { getDatadogMonitorIdFromIntegration } from 'utils/status-checks/status-check-integrations-util-datadog'
import { messageFromResponseError } from 'utils/strings'
import { ms } from 'utils/time/millisecond'

/**
 * These fields don't require re-testing by the user for the form to be considered valid.
 * Manual re-validation (via the user testing the connection and evaluation sections)
 * is required for any fields in the monitor/alert and success criteria sections.
 */
const FIELDS_THAT_DONT_REQUIRE_RETEST: (keyof StatusCheckFormValues)[] = [
  'category',
  'pollingIntervalSeconds',
  'statusCheckName'
]

const statusChecksUrls = getStatusChecksUrls()

export const EditStatusCheckForm: React.FC<{
  statusCheck: ServiceStatusCheckFromStatusCheck
}> = ({ statusCheck }) => {
  const isClone = statusCheck.id === null
  const location = useLocation()
  const navigate = useNavigate()

  const { isStatusCheckConnectionValidated, isStatusCheckEvaluationValidated } =
    useStatusCheckDetailsContext()

  const datadogMonitorId =
    getDatadogMonitorIdFromIntegration(statusCheck.integration) ?? undefined

  // Form values and methods
  const { formState, getFieldState, handleSubmit } =
    useFormContext<StatusCheckFormValues>()
  const { dirtyFields, isDirty, isSubmitting, isValid } = formState
  const dirtyFieldNames = Object.keys(dirtyFields)
  const statusCheckNameFieldState = getFieldState('statusCheckName', formState)
  const pollingIntervalFieldState = getFieldState(
    'pollingIntervalSeconds',
    formState
  )

  // Determines if the base criteria for a valid form change has been met.
  const canSubmitForm =
    isStatusCheckConnectionValidated &&
    isStatusCheckEvaluationValidated &&
    isDirty &&
    isValid &&
    !isSubmitting

  // Certain field changes should not require endpoint/evaluation validation checks in order to submit.
  const canOverrideSubmitForm = Boolean(
    isDirty &&
      !difference(dirtyFieldNames, FIELDS_THAT_DONT_REQUIRE_RETEST).length &&
      !statusCheckNameFieldState.error &&
      !pollingIntervalFieldState.error
  )

  const { referrer = statusChecksUrls.base() } = location.state ?? {}
  const { refetch: refetchStatusChecks } = useGetStatusChecks({
    enabled: false
  })
  const { mutateAsync: createStatusCheck } = useCreateStatusCheck()
  const { mutateAsync: updateStatusCheck } = usePutStatusCheck(statusCheck.id)
  const { mutateAsync: createDynatraceStatusCheck } =
    useCreateDynatraceStatusCheck()
  const { mutateAsync: updateDynatraceStatusCheck } =
    useUpdateDynatraceStatusCheck(statusCheck.id)

  const buildSaveStatusCheckRequest = useStatusCheckRequestBuilder()
  const handleSaveStatusCheck = async (values: StatusCheckFormValues) => {
    try {
      const request = await buildSaveStatusCheckRequest(values)
      const isDynatraceIntelligentHealthCheckRequest =
        getIsDynatraceIntelligentHealthCheckRequest(request)

      if (isClone) {
        isDynatraceIntelligentHealthCheckRequest
          ? await createDynatraceStatusCheck(request)
          : await createStatusCheck(request)
      } else {
        isDynatraceIntelligentHealthCheckRequest
          ? await updateDynatraceStatusCheck(request)
          : await updateStatusCheck(request)
      }

      toast({
        type: 'success',
        text: 'Health Check saved successfully.',
        time: ms.seconds(2)
      })

      // Refresh status checks list
      await handleDataFetchOnChange(refetchStatusChecks)

      // Navigate to back to status check list
      navigate(referrer)
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an issue saving this Health Check. Please try again later.'
      )
      toast({ type: 'error', text: error })
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSaveStatusCheck)}>
      <Grid container>
        <Grid item lg={9} xs={12}>
          <Stack sx={{ rowGap: 3 }}>
            {datadogMonitorId ? (
              <MonitorDetailsDatadog
                monitorId={datadogMonitorId}
                statusCheck={statusCheck}
              />
            ) : (
              <MonitorDetails statusCheck={statusCheck} />
            )}
            <StatusCheckConnectionForm />
            <Stack direction="row" sx={{ columnGap: 2 }}>
              <LoadingButton
                data-testid="edit-status-check-save-button"
                isDisabled={!canSubmitForm && !canOverrideSubmitForm}
                isLoading={isSubmitting}
                type="submit"
                variant="contained"
              >
                {isSubmitting ? ' Saving...' : 'Save Health Check'}
              </LoadingButton>
              <Button
                color="secondary"
                component={StyledRouterLink}
                disabled={isSubmitting}
                to={referrer === '/' ? statusChecksUrls.list() : referrer}
                variant="contained"
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}
